import React, { useState, useEffect } from 'react';
import { ExternalLink, Award, Star, ChevronLeft, ChevronRight } from 'lucide-react';
import { referenceProjects as fallbackProjects } from '../../data/mock';
import { useLanguage } from '../../context/LanguageContext';
import { AnimateOnScroll } from '../../hooks/useScrollAnimation';
import axios from 'axios';

const ReferenceSection = () => {
  const { t, language } = useLanguage();
  const [current, setCurrent] = useState(0);
  const [projects, setProjects] = useState(fallbackProjects);

  useEffect(() => {
    const timer = setTimeout(async () => {
      try {
        const url = process.env.REACT_APP_BACKEND_URL || '';
        const res = await axios.get(`${url}/api/content/references`);
        if (res.data && res.data.length > 0) setProjects(res.data);
      } catch (e) { /* fallback */ }
    }, 200);
    return () => clearTimeout(timer);
  }, []);

  const next = () => setCurrent((prev) => (prev + 1) % projects.length);
  const prev = () => setCurrent((prev) => (prev - 1 + projects.length) % projects.length);

  if (projects.length === 0) return null;

  const project = projects[current];
  const desc = project[`description_${language}`] || project.description_de || '';
  const features = project[`features_${language}`] || project.features_de || [];

  return (
    <section id="reference" className="py-24 lg:py-32 bg-gradient-to-b from-mid-purple/5 to-white dark:from-gray-800 dark:to-gray-900 relative overflow-hidden">
      <div className="absolute top-10 right-10 w-64 h-64 bg-mid-purple/10 rounded-full blur-hero" />
      <div className="absolute bottom-10 left-10 w-72 h-72 bg-mid-blue/10 rounded-full blur-hero" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
        <div className="text-center mb-16">
          <AnimateOnScroll animation="fade-up">
            <span className="inline-flex items-center gap-2 bg-gradient-to-r from-mid-purple/20 to-mid-pink/20 text-mid-purple px-4 py-1.5 rounded-full text-xs font-mono uppercase tracking-wider mb-4 border border-mid-purple/20">
              <Award size={14} />{t('reference.badge')}
            </span>
          </AnimateOnScroll>
          <AnimateOnScroll animation="fade-up" delay={0.1}>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-black dark:text-white mb-4">
              {t('reference.title')} <span className="gradient-text">{t('reference.titleHighlight')}</span>
            </h2>
          </AnimateOnScroll>
          <AnimateOnScroll animation="fade-up" delay={0.2}>
            <p className="text-dark-grey dark:text-gray-400 text-lg max-w-2xl mx-auto">{t('reference.subtitle')}</p>
          </AnimateOnScroll>
        </div>

        <AnimateOnScroll animation="scale" delay={0.2}>
          <div className="max-w-4xl mx-auto relative">
            <a href={project.url} target={project.url !== '#' ? '_blank' : undefined} rel="noopener noreferrer" className="group block" data-testid={`reference-card-${current}`}>
              <div className="relative rounded-3xl overflow-hidden shadow-2xl bg-white dark:bg-gray-800 border border-grey/10 dark:border-gray-700 card-hover">
                <div className="absolute top-6 left-6 z-20 flex items-center gap-2 px-4 py-2 bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm rounded-full shadow-lg">
                  <Star size={16} className="text-mid-yellow fill-mid-yellow" />
                  <span className="text-sm font-medium text-black dark:text-white">{t('reference.createdWith')}</span>
                </div>
                <div className="relative h-64 md:h-80 overflow-hidden">
                  <img src={project.image} alt={project.title} className="w-full h-full object-cover img-zoom" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-8">
                    <h3 className="text-3xl md:text-4xl font-display font-bold text-white mb-2 flex items-center gap-3">
                      {project.title}
                      {project.url !== '#' && <ExternalLink size={24} className="opacity-0 group-hover:opacity-100 transition-opacity" />}
                    </h3>
                    <p className="text-white/80 text-lg">{desc}</p>
                  </div>
                </div>
                <div className="p-8 bg-white dark:bg-gray-800">
                  <div className="flex flex-wrap gap-3">
                    {(Array.isArray(features) ? features : []).map((feature, i) => (
                      <span key={i} className="px-4 py-2 bg-mid-purple/10 dark:bg-mid-purple/20 text-mid-purple rounded-full text-sm font-medium">{feature}</span>
                    ))}
                  </div>
                  {project.url !== '#' && (
                    <div className="mt-6 flex justify-center">
                      <span className="inline-flex items-center gap-2 text-black dark:text-white font-mono text-sm uppercase tracking-wider group-hover:text-mid-purple transition-colors">
                        {t('reference.visitSite')}<ExternalLink size={16} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </a>
            {projects.length > 1 && (
              <>
                <button onClick={e => { e.preventDefault(); prev(); }} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-6 w-10 h-10 rounded-full bg-white dark:bg-gray-800 shadow-lg border border-grey/20 dark:border-gray-700 flex items-center justify-center hover:scale-110 transition-transform z-30" data-testid="carousel-prev"><ChevronLeft size={20} className="text-black dark:text-white" /></button>
                <button onClick={e => { e.preventDefault(); next(); }} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-6 w-10 h-10 rounded-full bg-white dark:bg-gray-800 shadow-lg border border-grey/20 dark:border-gray-700 flex items-center justify-center hover:scale-110 transition-transform z-30" data-testid="carousel-next"><ChevronRight size={20} className="text-black dark:text-white" /></button>
                <div className="flex justify-center gap-2 mt-6">
                  {projects.map((_, i) => <button key={i} onClick={() => setCurrent(i)} className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${i === current ? 'bg-mid-purple w-8' : 'bg-grey/30 dark:bg-gray-600 hover:bg-grey/50'}`} data-testid={`carousel-dot-${i}`} />)}
                </div>
              </>
            )}
          </div>
        </AnimateOnScroll>
      </div>
    </section>
  );
};

export default ReferenceSection;
